import { type FC, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { initializeApolloClient } from '@crehana/apollo-client';
import type { Me } from '@crehana/auth';
import { getImgxUrl } from '@crehana/compass.ui';
import { PageLoader } from '@crehana/compass.ui/v2';
import {
  getG11nAndTranslationsFromServer,
  type TCustomGetServerSideProps,
} from '@crehana/next';
import { useMe } from '@crehana/web';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import HeaderOnboarding from '@/shared/components/HeaderOnboarding';
import useActivateUser from '@/shared/hooks/useActivateUser/useActivateUser';
import ACTIVATE_ACCOUNT_MUTATION from '@/shared/hooks/useActiveAccount/graphql/ActivateAccount.v5.graphql';
import type { ActivateAccount } from '@/shared/hooks/useActiveAccount/graphql/types/ActivateAccount';
import useGetUserOrganizationQuery from '@/shared/hooks/useGetUserOrganizationQuery/useGetUserOrganizationQuery';
import type { UserOrganizationStatus } from '@/shared/types/graphql/globals.v5';
import type { AuthPagesOrgInfoQuery_organizationInfo as OrgInfoType } from '@/views/Auth/graphql/types/AuthPagesOrgInfoQuery';
import { withOrgInfo, withSeo } from '@/views/Auth/hocs';
import { useFlagOrganization } from '@/views/Auth/hooks';
import routes from '@/views/Auth/routes';

import { PoweredBy, Stepper } from './components';
import { localeEn, localeEs, localePt } from './i18n';
import { Features, Landing, Resources } from './sections';

const PAGE_KEY = 'crehana-frontend-dashboard-onboarding-welcome';

export interface WelcomeProps {
  orgInfo: OrgInfoType;
}

const WelcomePage: FC<React.PropsWithChildren<WelcomeProps>> = ({
  orgInfo,
}) => {
  const router = useRouter();
  const { me, loading: loadingUseMe } = useMe();
  const { slug } = router.query as { slug: string };

  const primaryColor = orgInfo?.lookAndFeel?.primary?.main || undefined;
  const nameOrg = orgInfo?.name || 'logo-name';

  const [currentStep, setCurrentSep] = useState(1);
  const [showLoading, setShowLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(
    null,
  );

  const { logEvent } = useAmplitude();

  const {
    userIdCentralized,
    userStatusCentralized,
    loading: loadingUser,
  } = useGetUserOrganizationQuery();

  const { isActive: isActiveOnboardingB2B } = useFlagOrganization({
    flag: 'onboarding-cross-experience',
  });

  const {
    activateUserCentralized,
    isLoadingCentralized: isLoadingActivationCentralized,
  } = useActivateUser();

  const logoOrg = getImgxUrl(orgInfo?.logoThumbnail || '', {
    auto: 'format',
    h: 56,
    w: 'auto',
    dpr: 2,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    if (me && slug && userStatusCentralized) {
      validRedirect(userStatusCentralized, me);
    }
  }, [me, slug, userStatusCentralized]);

  const validRedirect = (
    userStatusCentralized: UserOrganizationStatus,
    me: Me,
  ) => {
    if (
      (userStatusCentralized === 'ACTIVE' && isActiveOnboardingB2B) ||
      me?.statusB2B === 'ACTIVE'
    ) {
      window.location.href = routes.onboardingNextUrl();
    }
  };

  const [activateAccount, { loading: isLoadingActivation }] =
    useMutation<ActivateAccount>(ACTIVATE_ACCOUNT_MUTATION, {
      context: { clientName: 'v5' },
      onCompleted({ activate_learning_user }) {
        if (
          activate_learning_user?.activate_learning_user?.__typename ===
            'UserOrganizationMutationPayload' &&
          activate_learning_user?.activate_learning_user?.response
        ) {
          logEvent({
            event: 'Onboarding Completed',
            data: {
              ...getSourceEventParams(),
              // @ts-expect-error Me is not a MeObject but has the same properties
              ...getCommonAmplitudeProperties(me),
              'Organization Slug': slug,
              'Organization Name': nameOrg,
            },
          });
          window.location.href = routes.onboardingNextUrl();
          setShowLoading(true);
        } else if (
          activate_learning_user?.activate_learning_user?.__typename ===
            'UserOrganizationValidationMutationError' ||
          activate_learning_user?.activate_learning_user?.__typename ===
            'InternalServerMutationError'
        ) {
          const errorList =
            activate_learning_user?.activate_learning_user?.errors || [];
          const errors =
            errorList.length > 1
              ? errorList.join(', ')
              : errorList[0] || 'Unknown error';
          setErrorMessage(errors);
        } else {
          setErrorMessage('Unknown error');
        }
      },
      onError(error) {
        console.log(error);
      },
    });

  const activateUserByFlag = async () => {
    if (isActiveOnboardingB2B && userIdCentralized) {
      try {
        const { data } = await activateUserCentralized({
          variables: {
            organizationSlug: slug,
            userId: userIdCentralized,
          },
        });
        if (data?.activate_user?.success) {
          logEvent({
            event: 'Onboarding Completed - User Centralized',
            data: {
              ...getSourceEventParams(),
              // @ts-expect-error Me is not a MeObject but has the same properties
              ...getCommonAmplitudeProperties(me),
              'Organization Slug': slug,
              'Organization Id': userIdCentralized,
            },
          });
          window.location.href = routes.onboardingNextUrl();
          setShowLoading(true);
        } else {
          setErrorMessage(
            data?.activate_user?.response?.message || 'Unknown error',
          );
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      activateAccount();
    }
  };

  const STEPS = {
    1: (
      <Landing
        me={me}
        setCurrentSep={setCurrentSep}
        firstName={me?.firstName || 'Hola'}
        organizationName={nameOrg}
        primaryColor={primaryColor}
      />
    ),
    2: <Features />,
    3: <Resources errorMessage={errorMessage} />,
  };

  if (
    loadingUseMe ||
    loadingUser ||
    showLoading ||
    isLoadingActivation ||
    isLoadingActivationCentralized
  ) {
    return <PageLoader />;
  }

  return (
    <div className="tw-flex tw-flex-col tw-relative tw-h-full">
      <NextSeo title="Bienvenida Panel de Administación" />
      <HeaderOnboarding
        step={currentStep}
        logoOrg={logoOrg}
        nameOrg={nameOrg}
      />
      {STEPS[currentStep as keyof typeof STEPS]}
      {currentStep > 1 && (
        <Stepper
          currentStep={currentStep}
          setCurrentSep={setCurrentSep}
          activateUserByFlag={activateUserByFlag}
          primaryColor={primaryColor}
        />
      )}
      <PoweredBy />
    </div>
  );
};

const App: FC<React.PropsWithChildren<WelcomeProps>> = ({ orgInfo }) => {
  return <WelcomePage orgInfo={orgInfo} />;
};

export const getServerSideProps: TCustomGetServerSideProps = async ({
  req,
  query,
}) => {
  const { slug } = query as { slug: string };
  const apolloClient = initializeApolloClient({
    headers: req.headers,
    pageKey: PAGE_KEY,
  });
  const i18nData = await getG11nAndTranslationsFromServer({
    apolloClient,
    headers: req.headers,
    pageKey: PAGE_KEY,
    defaultTranslations: {
      es: { [PAGE_KEY]: { ...localeEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt } },
      en: { [PAGE_KEY]: { ...localeEn.en } },
    },
  });

  return {
    props: {
      ...i18nData,
      organizationSlug: slug,
    },
  };
};

export default withSeo(withOrgInfo(App));
